import { SlidersHorizontal } from "lucide-react";
import type { FC } from "react";

import {
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger
} from "@/components/ui/dropdown-menu";

import { formatExhibitTokenBare } from "../../../helpers/exhibitNumbering";
import { formatExhibitLabel, labelStyle } from "../../caseFiles/exhibitLabels";
import {
  applyPreset,
  CUSTOM_STYLE_LABEL,
  EXHIBIT_STYLE_PRESETS,
  matchPreset,
  MORE_PRESETS,
  MORE_STYLES_LABEL,
  presetSamples,
  PRIMARY_PRESETS,
  type ExhibitStylePreset
} from "../../caseFiles/exhibitStylePresets";
import type { ExhibitScheme } from "../../caseFiles/types";

/** The case's first exhibit label ("A.", "Exhibit A"): how its style reads. */
export const exhibitSample = (scheme: ExhibitScheme): string =>
  formatExhibitLabel(
    formatExhibitTokenBare(scheme.start_at, labelStyle(scheme)),
    scheme
  );

type Props = {
  scheme: ExhibitScheme;
  onSchemeChange: (scheme: ExhibitScheme) => void;
  /** Opens the level builder; the menu closes first. */
  onCustom?: () => void;
};

/**
 * The exhibit label styles as menu radio items: the two primary styles, then
 * More Styles in a submenu, then Custom…. Picking one renumbers the tree at
 * once; the scheme saves in the background.
 */
export const ExhibitFormatItems: FC<Props> = ({
  scheme,
  onSchemeChange,
  onCustom
}) => {
  const current = matchPreset(scheme);
  const pick = (id: string) => {
    const preset = EXHIBIT_STYLE_PRESETS.find((p) => p.id === id);
    if (preset) onSchemeChange(applyPreset(scheme, preset));
  };
  const item = (preset: ExhibitStylePreset) => (
    <DropdownMenuRadioItem
      key={preset.id}
      value={preset.id}
      data-testid={`exhibit-style-${preset.id}`}
    >
      <span className="flex-1 truncate">{preset.label}</span>
      {preset.group === "more" ? (
        <span className="shrink-0 font-mono text-sm text-fg-4 tabular-nums">
          {presetSamples(scheme, preset, 2).join("  ")}
        </span>
      ) : null}
    </DropdownMenuRadioItem>
  );
  const moreCurrent = current?.group === "more";
  return (
    <>
      <DropdownMenuRadioGroup value={current?.id ?? ""} onValueChange={pick}>
        {PRIMARY_PRESETS.map(item)}
      </DropdownMenuRadioGroup>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger data-testid="exhibit-style-more">
          <span className="flex-1">{MORE_STYLES_LABEL}</span>
          {moreCurrent ? (
            <span className="max-w-24 truncate text-sm text-fg-4">
              {current.label}
            </span>
          ) : null}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent
          className="w-72"
          data-testid="exhibit-style-more-menu"
        >
          <DropdownMenuRadioGroup
            value={current?.id ?? ""}
            onValueChange={pick}
          >
            {MORE_PRESETS.map(item)}
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      {onCustom ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={onCustom}
            data-testid="exhibit-style-custom"
          >
            <SlidersHorizontal />
            {CUSTOM_STYLE_LABEL}
          </DropdownMenuItem>
        </>
      ) : null}
      {current ? null : (
        <p className="px-2 pt-1 pb-1.5 text-sm text-fg-3">
          This case uses a custom style. Picking one replaces it.
        </p>
      )}
    </>
  );
};
